import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, MenuDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

export const getMenuComponentTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): MenuDesignTokens => {
  const { spacing, tokens, typography } = theme ?? {};
  const { colors } = tokens ?? {};
  const backgrounds = {
    idle: 'none',
    hover: colors?.background.neutral.hover.value,
    active: colors?.background.neutral.pressed.value,
    selected: colors?.background.selected.subtler.value,
    selectedActive: colors?.background.selected.subtle.value,
    selectedHover: colors?.background.selected.subtle.value,
  };
  return {
    item: {
      _: {
        'padding-top': 0,
        'padding-bottom': 0,
        'padding-right': px(spacing.M),
        'padding-left': px(spacing.M),
        'border-radius': 2,
        'min-width': undefined,
        width: '100%',
        height: 36,
        color: colors?.text.primary.value,
        '&:disabled': {
          color: colors?.text.inactive.value,
          cursor: 'no-drop',
        },
      },
      backgrounds,
      icon: {
        _: {
          'margin-right': spacing.XS,
        },
        idle: { color: colors?.icon.secondary.value },
        selected: { color: colors?.icon.selected.value },
      },
      'has-children': {
        'padding-right': 40,
      },
      idle: {
        cursor: 'pointer',
        'font-family': typography.bodyMedium.fontFamily,
        'font-size': typography.bodyMedium.fontSize,
        'font-weight': `${typography.bodyMedium.fontWeight}`,
        'line-height': typography.bodyMedium.lineHeight,
      },
      selected: {
        cursor: 'pointer',
        'font-family': typography.bodyMediumBold.fontFamily,
        'font-size': typography.bodyMediumBold.fontSize,
        'font-weight': `${typography.bodyMediumBold.fontWeight}`,
        'line-height': typography.bodyMediumBold.lineHeight,
      },
    },
    category: {
      _: {
        'margin-top': 0,
        'margin-bottom': 0,
      },
      title: {
        _: {
          color: colors?.text.secondary.value,
          'margin-top': px(spacing.M),
          'margin-bottom': 0,
          'margin-right': px(spacing.M),
          'margin-left': px(spacing.M),
          'font-family': theme.typography.heading4.fontFamily,
          'font-size': theme.typography.heading4.fontSize,
          'font-weight': `${theme.typography.heading4.fontWeight}`,
          'line-height': theme.typography.heading4.lineHeight,
        },
        underline: {
          'padding-bottom': 5,
          'border-bottom-width': 1,
          'border-bottom-style': 'solid',
          'border-bottom-color': colors?.border.subtle.value,
        },
      },
    },
    list: {
      'padding-top': px(spacing.XS),
      'padding-bottom': px(spacing.XS),
      'padding-left': 0,
      'padding-right': 0,
      'margin-top': 0,
      'margin-bottom': 0,
      'margin-left': 0,
      'margin-right': 0,
      'list-style': 'none',
    },
    buttons: {
      split: {
        _: {
          'border-top-left-radius': theme.borderRadius.small,
          'border-bottom-left-radius': theme.borderRadius.small,
          'border-top-right-radius': theme.borderRadius.none,
          'border-bottom-right-radius': theme.borderRadius.none,
        },
        toggle: {
          'border-top-left-radius': theme.borderRadius.none,
          'border-bottom-left-radius': theme.borderRadius.none,
          'border-top-right-radius': theme.borderRadius.small,
          'border-bottom-right-radius': theme.borderRadius.small,
        },
        primary: {
          'border-right-width': 1,
          'border-right-style': 'solid',
          'border-right-color': colors?.border.inverse.value,
        },
        basic: {
          'border-right-style': 'none',
        },
      },
      action: {
        open: {
          background: colors?.background.neutral.selected.value,
        },
        closed: {
          background: colors?.background.primary.value,
        },
      },
    },
  };
};

export const getMenuComponentTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): MenuDesignTokens => {
  const {
    semantic,
    generic,
    component: { menu },
  } = tokens ?? { component: {} };

  const bg = menu?.['background-color'];
  const fontWeight = menu?.['font-weight'];
  const iconFill = menu?.checkmark?.icon?.fill;
  const header = menu?.header;

  const font = {
    'font-family': fontFamily,
    'font-size': menu?.['font-size'],
    'line-height': menu?.['line-height'],
  };

  const backgrounds = {
    idle: bg?.default,
    hover: bg?.hover,
    active: bg?.pressed,
    selected: bg?.hover,
    selectedActive: bg?.pressed,
    selectedHover: bg?.pressed,
  };

  return {
    _: {
      background: bg?.default,
      color: menu?.['text-color'],
    },
    item: {
      _: {
        'padding-top': generic?.spacing?.[8],
        'padding-bottom': generic?.spacing?.[8],
        'padding-right': header?.['padding-right'],
        'padding-left': header?.['padding-left'],
        'border-radius': 0,
        'min-width': menu?.['min-width'],
        width: '100%',
        height: menu?.['min-height'],
        color: menu?.['text-color'],
        '&:disabled': {
          cursor: 'no-drop',
          opacity: menu?.opacity?.disabled,
        },
      },
      icon: {
        _: {
          'margin-right': menu?.icon?.['margin-right'],
        },
        idle: { color: menu?.icon?.fill },
        selected: { color: iconFill?.selected ?? menu?.icon?.fill },
      },
      'has-children': {
        'padding-right': '40px',
      },
      idle: {
        cursor: 'pointer',
        ...font,
        'font-weight': fontWeight?.default,
      },
      selected: {
        cursor: 'pointer',
        ...font,
        'font-weight': fontWeight?.selected,
      },
      backgrounds,
    },
    category: {
      _: {
        'margin-top': menu?.divider['margin-top'],
        'margin-bottom': menu?.divider['margin-bottom'],
      },
      title: {
        _: {
          color: menu?.header?.['text-color'],
          'padding-top': generic?.spacing?.[4],
          'padding-bottom': generic?.spacing?.[4],
          'padding-right': header?.['padding-right'],
          'padding-left': header?.['padding-left'],
          'margin-bottom': 0,
          'margin-top': 0,
          'margin-left': 0,
          'margin-right': 0,
          'font-family': fontFamily,
          'font-size': menu?.header?.['font-size'],
          'line-height': menu?.header?.['line-height'],
          'font-weight': menu?.header?.['font-weight'],
          'text-transform': 'uppercase',
        },
        underline: {
          'padding-bottom': 5,
          'border-bottom-width': 1,
          'border-bottom-style': 'solid',
          'border-bottom-color': semantic?.['border-color']?.divider?.light,
        },
      },
    },
    list: {
      'padding-left': 0,
      'padding-right': 0,
      'margin-top': 0,
      'margin-bottom': 0,
      'margin-left': 0,
      'margin-right': 0,
      'list-style': 'none',
    },
    buttons: {
      split: {
        _: {
          'border-top-left-radius': generic?.['border-radius'][4],
          'border-bottom-left-radius': generic?.['border-radius'][4],
          'border-top-right-radius': generic?.['border-radius'].none,
          'border-bottom-right-radius': generic?.['border-radius'].none,
        },
        toggle: {
          'border-top-left-radius': generic?.['border-radius'].none,
          'border-bottom-left-radius': generic?.['border-radius'].none,
          'border-top-right-radius': generic?.['border-radius'][4],
          'border-bottom-right-radius': generic?.['border-radius'][4],
        },
        basic: {
          'border-right-style': 'none',
        },
        primary: {
          'border-right-width': 1,
          'border-right-style': 'solid',
          'border-right-color': semantic?.['border-color']?.divider?.heavy,
        },
      },
      action: {
        open: {
          background: bg?.pressed,
        },
        closed: {
          background: bg?.default,
        },
      },
    },
  };
};
